import {
	ReactElement,
	RefObject,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import {
	DateString,
	Icon,
	Pagination,
	PersonAvatar,
	TableView,
	TableViewNoResultConfig,
	Tag
} from "client/Components";
import { contains } from "client/Data/searching";
import {
	createLocalDataSource,
	useFilterHandler,
	useSelection,
	useSortHandler,
	useUserTimezone,
	useValueEqualMemo
} from "client/Hooks";
import { localize } from "client_legacy/ndaw/culture";
import { DataSourceSortableState, DataSourceSortDirection } from "client/Data/sorting";
import {
	EMPTY_ARRAY,
	generateTrackingId,
	isNil,
	isNotNil
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { UserGridItem } from "client/Api/UserManagement/userManagement";
import { TimeZone } from "client/DateAndTime/timezones";
import { UserLicense } from "client/Api/FeaturePackages/featurePackageManagement";
import { UserLicenseFilterForm } from "./UserLicenseFilterForm";
import {
	UserDisplayItem,
	convertToDisplayItem
} from "./misc";
import { FirstnameCellContent } from "./FirstnameCellContent";

const {
	Row,
	Cell
} = TableView;

export type UserOverviewSelectionProps = {
	selection: readonly UserGridItem[];
	onSelectionChange(selection: readonly UserGridItem[]): void;
} | {
	selection?: undefined;
	onSelectionChange?: undefined;
};

export type UserOverviewProps = {
	data: readonly UserGridItem[];
	searchTerm: string;
	showDeletionColumns?: boolean;
	height?: "fill" | "stretch";
	requiredUserLicense?: UserLicense | null;
	hasNoUserLicenseFilteringAndSorting?: boolean;
} & UserOverviewSelectionProps;

export const UserOverview = (props: UserOverviewProps): ReactElement => {
	const {
		data,
		searchTerm,
		selection,
		onSelectionChange,
		showDeletionColumns = false,
		height,
		requiredUserLicense,
		hasNoUserLicenseFilteringAndSorting = false
	} = props;

	const [filterState, setFilterState] = useState<FilterState>({ licenses: [] });
	const [sortingState, setSortingState] = useState<DataSourceSortableState<UserDisplayItem>>({
		propertyName: "firstName",
		direction: DataSourceSortDirection.Ascending
	});
	const [currentPageNumber, setCurrentPageNumber] = useState<number>(0);

	const userTimezone: TimeZone = useUserTimezone();

	const bodyRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

	const { filteringFor } = useFilterHandler<FilterState>(filterState, setFilterState);
	const { sortingFor } = useSortHandler<UserDisplayItem>(sortingState, setSortingState);

	const displayItems = useMemo(
		(): readonly UserDisplayItem[] => {
			return data.map((x) => convertToDisplayItem(x, requiredUserLicense));
		},
		[data, requiredUserLicense]
	);

	const { currentItems: currentDisplayItems, pagination } = useDataSource(
		displayItems,
		{
			searchTerm,
			sorting: sortingState,
			pagination: {
				amountPerPage: 100,
				currentPageNumber
			},
			filtering: filterState
		}
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(searchTerm);
			setCurrentPageNumber(0);
		},
		[searchTerm]
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(
				currentPageNumber,
				sortingState,
				searchTerm
			);

			bodyRef.current
				?.scrollTo(0, 0);
		},
		[
			currentPageNumber,
			sortingState,
			searchTerm
		]
	);

	const currentItems: readonly UserGridItem[] = useMemo(
		(): readonly UserGridItem[] => currentDisplayItems.map((x) => x.model),
		[currentDisplayItems]
	);

	const stableCurrentItems = useValueEqualMemo(
		(): readonly UserGridItem[] => currentItems,
		[currentItems]
	);

	const { selectionFor, multiSelection } = useSelection(
		stableCurrentItems,
		"id",
		{
			isMultiSelect: true,
			selectedItems: selection ?? EMPTY_ARRAY,
			setSelectedItems: onSelectionChange ?? notAvailableSelectionHandler,
			hasMemorization: true,
			allItems: data
		}
	);

	const noResultConfig = useMemo(
		(): TableViewNoResultConfig => {
			if (searchTerm) {
				return {
					icon: "magnifying-glass",
					title: localize("_NoSearchResults"),
					message: localize("_PleaseTrySearchingWithAnotherTerm")
				};
			}

			return {
				icon: "user",
				title: localize(
					showDeletionColumns
						? "_NoDeletedUsersYet"
						: "_NoUsersYet"
				),
				message: localize(
					showDeletionColumns
						? "_NoUsersHaveBeenDeletedYet"
						: "_NoUsersHaveBeenAddedYet"
				)
			};
		},
		[searchTerm, showDeletionColumns]
	);

	return (
		<>
			<TableView
				multiSelectable={isNotNil(selection) && multiSelection}
				trackingId="user-overview"
				columns={[
					{
						title: "",
						width: 40,
						minWidth: 40,
						trackingId: "avatar"
					},
					{
						title: localize("_FirstName"),
						sortable: sortingFor("firstName"),
						trackingId: "first-name"
					},
					{
						title: localize("_LastName"),
						sortable: sortingFor("lastName"),
						trackingId: "last-name"
					},
					{
						title: localize("_Email"),
						sortable: sortingFor("primaryEmail"),
						trackingId: "email"
					},
					{
						title: localize("_Username"),
						sortable: sortingFor("userPrincipalName"),
						trackingId: "username"
					},
					{
						title: localize("_Status"),
						sortable: sortingFor("isActive"),
						trackingId: "is-active"
					},
					{
						title: localize("_Package"),
						sortable: hasNoUserLicenseFilteringAndSorting
							? false
							: sortingFor("userLicenseTag.value"),
						trackingId: "package",
						filterable: hasNoUserLicenseFilteringAndSorting
							? false
							: {
								columnFilterableFormFactory: (onClose) => (
									<UserLicenseFilterForm
										filterable={filteringFor("licenses")}
										onClose={onClose}
									/>
								),
								isActive: filterState.licenses.length > 0
							}
					},
					{
						title: localize("_GuestUser"),
						sortable: sortingFor("isGuest"),
						trackingId: "is-guest"
					},
					{
						title: localize("_Administrator"),
						sortable: sortingFor("isAdmin"),
						trackingId: "is-admin"
					},
					showDeletionColumns && {
						title: localize("_DeletionDate"),
						sortable: sortingFor("deletionDate"),
						trackingId: "deletion-date"
					},
					showDeletionColumns && {
						title: localize("_PermanentDeletionDate"),
						sortable: sortingFor("permanentDeletionDate"),
						trackingId: "permanent-deletion-date"
					}
				]}
				items={currentDisplayItems}
				itemKey="id"
				ref={bodyRef}
				height={height}
				noResultConfig={noResultConfig}
			>
				{
					(x): ReactElement => (
						<Row
							selectable={isNotNil(selection) && selectionFor(x.model)}
							trackingId={x.id.toString()}
						>
							<Cell>
								<PersonAvatar
									imageSrc={x.avatarUrl}
									initials={x.initials}
									size="x-small"
								/>
							</Cell>
							<Cell tooltip={x.firstName}>
								<FirstnameCellContent item={x} />
							</Cell>
							<Cell tooltip={x.lastName}>{x.lastName}</Cell>
							<Cell tooltip={x.primaryEmail ?? undefined}>{x.primaryEmail}</Cell>
							<Cell tooltip={x.userPrincipalName}>{x.userPrincipalName}</Cell>
							<Cell>
								<Tag
									text={localize(x.isActive ? "_Active" : "_Inactive")}
									color={x.isActive ? "success" : "danger"}
								/>
							</Cell>
							<Cell>
								<Tag
									{...x.userLicenseTag}
									trackingId={generateTrackingId("package-tag", `|${x.id}`)}
								/>
							</Cell>

							<Cell>
								{x.isGuest && <Icon value="check" size="large" />}
							</Cell>

							<Cell>
								{x.isAdmin && <Icon value="check" size="large" />}
							</Cell>
							{showDeletionColumns && (
								<>
									<Cell>
										{x.deletionDate && (
											<DateString
												value={x.deletionDate}
												format="DMYT"
												timezone={userTimezone}
											/>
										)}
									</Cell>

									<Cell>
										{x.permanentDeletionDate && (
											<DateString
												value={x.permanentDeletionDate}
												format="DMYT"
												timezone={userTimezone}
											/>
										)}
									</Cell>
								</>
							)}
						</Row>
					)
				}
			</TableView>
			<Pagination {...pagination} onPageNumberChanged={setCurrentPageNumber} />
		</>
	);
};
UserOverview.displayName = "UserOverview";

type FilterState = {
	licenses: readonly UserLicense[];
};

function applyFilter(
	items: readonly UserDisplayItem[],
	filter: FilterState
): readonly UserDisplayItem[] {
	if (filter.licenses.length === 0) {
		return items;
	}

	return items.filter(
		item => {
			const userLicense: UserLicense | null = item.userLicenseTag.value;

			if (isNil(userLicense)) {
				return false;
			}

			return filter.licenses
				.includes(userLicense);
		}
	);
}

const useDataSource = createLocalDataSource<UserDisplayItem, FilterState>()
	.setConfiguration({
		search: {
			doesMatchItem: (item, searchTerm) =>
				contains(item.firstName, searchTerm)
				|| contains(item.lastName, searchTerm)
				|| contains(item.userPrincipalName, searchTerm)
				|| contains(item.primaryEmail ?? "", searchTerm)
		},
		filtering: {
			applyFilter
		},
		hasPagination: true
	})
	.getHook();

const notAvailableSelectionHandler = (selection: readonly UserGridItem[]): void => {
	if (selection !== EMPTY_ARRAY) {
		console.error("Selection is not supported and ignored", selection);
	}
};

export const userOverviewSearchPropertyKeys: readonly [LocalizationKey, ...LocalizationKey[]] = [
	"_Name",
	"_Email",
	"_Username"
];
